import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ItemType, UploadType } from 'api/Responses/BulkUploadResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import Button from 'components/Button';
import PdfPreview from 'components/PdfPreview/PdfPreview';
import { useLanguage } from 'context/AppContext';
import { StorageType } from 'utils/helpers/image.helpers';
import * as Styles from './MergeConflictModal.styles';

interface SetModalProps {
  open: boolean;
  close: () => void;
  draft: ItemType;
  handleApproveDuplicate: Function;
  handleIgnoreDuplicate: Function;
  uploadType: UploadType;
}
export type ModalContentType = 'selectItem' | 'selectTray' | 'selectPart';

const MergeConflictModal: FC<SetModalProps> = ({
  open,
  close,
  draft,
  handleApproveDuplicate,
  handleIgnoreDuplicate,
  uploadType,
}) => {
  const [language] = useLanguage();
  const [valuesExisting, setValuesExisting] = useState<any[]>([]);
  const [valuesUpdated, setValuesUpdated] = useState<any[]>([]);

  // Closes the modal and resets the modal state after animation
  const handleClose = (): void => {
    close();
  };

  const approveDuplicate = (): void => {
    handleApproveDuplicate(draft);
    close();
  };

  const ignoreDuplicate = (): void => {
    handleIgnoreDuplicate(draft);
    close();
  };

  const rowText = draft.attachment_type === 'image' ? 'Tray Reference Image' : 'Set Manual';

  useEffect(() => {
    if (uploadType === UploadType['Set Upload']) {
      setValuesUpdated([
        { key: 'Set Code', value: draft?.updated_data?.set_code },
        { key: 'Description', value: draft?.updated_data?.description },
        { key: 'Number of Trays', value: draft?.updated_data?.num_trays },
        { key: 'Number of Devices', value: draft?.updated_data?.num_devices },
      ]);
      setValuesExisting([
        { key: 'Set Code', value: draft?.existing_data?.set_code },
        { key: 'Description', value: draft?.existing_data?.description },
        { key: 'Number of Trays', value: draft?.existing_data?.num_trays },
        { key: 'Number of Devices', value: draft?.existing_data?.num_devices },
      ]);
    }
    if (uploadType === UploadType['Device Upload']) {
      setValuesUpdated([
        { key: 'Device ID', value: draft?.updated_data?.device_id },
        { key: 'Description', value: draft?.updated_data?.description },
        { key: 'Number of Parts', value: draft?.updated_data?.num_parts },
      ]);
      setValuesExisting([
        { key: 'Device ID', value: draft?.existing_data?.device_id },
        { key: 'Description', value: draft?.existing_data?.description },
        { key: 'Number of Parts', value: draft?.existing_data?.num_parts },
      ]);
    }
    if (uploadType === UploadType['Set Attachments']) {
      setValuesUpdated([
        {
          key: rowText,
          value:
            draft.attachment_type === 'image' ? (
              <AsyncImage
                src={draft?.new_gs_location}
                alt={'alt'}
                imgWidth={18}
                imgHeight={11}
                storageType={StorageType.FIREBASE_GCS_INCLUDED}
              />
            ) : (
              <PdfPreview firebase deviceId={draft.device_id} fileUrl={draft?.new_gs_location} />
            ),
        },
      ]);
      setValuesExisting([
        {
          key: rowText,
          value:
            draft?.attachment_type === 'image' ? (
              <AsyncImage
                src={draft?.existing_gs_location}
                alt={'alt'}
                imgWidth={18}
                imgHeight={11}
                storageType={StorageType.FIREBASE_GCS_INCLUDED}
              />
            ) : (
              <PdfPreview firebase deviceId={draft.device_id} fileUrl={draft?.existing_gs_location} />
            ),
        },
      ]);
    }
    if (uploadType === UploadType['Device Attachments']) {
      setValuesUpdated([
        {
          key: rowText,
          value:
            draft?.attachment_type === 'image' ? (
              <AsyncImage
                src={draft?.new_gs_location}
                alt={'alt'}
                imgWidth={18}
                imgHeight={11}
                storageType={StorageType.FIREBASE_GCS_INCLUDED}
              />
            ) : (
              <PdfPreview firebase deviceId={draft.device_id} fileUrl={draft?.new_gs_location} />
            ),
        },
      ]);
      setValuesExisting([
        {
          key: rowText,
          value:
            draft?.attachment_type === 'image' ? (
              <AsyncImage
                src={draft?.existing_gs_location}
                alt={'alt'}
                imgWidth={18}
                imgHeight={11}
                storageType={StorageType.FIREBASE_GCS_INCLUDED}
              />
            ) : (
              <PdfPreview firebase deviceId={draft.device_id} fileUrl={draft?.existing_gs_location} />
            ),
        },
      ]);
    }
    if (uploadType === UploadType['Set Serial Numbers']) {
      setValuesUpdated([
        { key: 'Set Code', value: draft?.updated_data?.set_code },
        { key: 'Set Serial', value: draft?.updated_data?.set_uss },
      ]);
      setValuesExisting([
        { key: 'Set Code', value: draft?.existing_data?.set_code },
        { key: 'Set Serial', value: draft?.existing_data?.set_uss },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={open} maxWidth="xl" fullWidth onClose={handleClose}>
      <Styles.ModalContent>
        <Box sx={{ display: 'flex', justifyContent: 'between', verticalAlign: 'center', paddingBottom: '20px' }}>
          <Typography variant="h5">
            {language.DataUpload.compareDuplicateUploadModal.title + ' '}{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {draft.device_id}
            </Box>
          </Typography>
        </Box>
        <Grid container spacing={2} columns={16}>
          <Grid item xs={8}>
            <Box sx={{ paddingBottom: '20px' }}>
              <Typography variant="h6">{language.DataUpload.deviceConflictModal.existingDataTitle}</Typography>
              <Typography variant="body1">{language.DataUpload.deviceConflictModal.existingDataSubtitle}</Typography>
            </Box>
            <Styles.DropTableContainer>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{language.DataUpload.deviceConflictModal.field}</TableCell>
                      <TableCell>{language.DataUpload.deviceConflictModal.input}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {valuesExisting?.map((item: any) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <div className="tableCell">{item.key}</div>
                        </TableCell>
                        <TableCell>
                          <div className="tableCell">{item.value}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Styles.DropTableContainer>
          </Grid>
          <Grid item xs={8}>
            <Box sx={{ paddingBottom: '20px' }}>
              <Typography variant="h6">{language.DataUpload.deviceConflictModal.updatedData}</Typography>
              <Typography variant="body1">{language.DataUpload.deviceConflictModal.updatedDataSubtitle}</Typography>
            </Box>
            <Styles.DropTableContainer>
              <TableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{language.DataUpload.deviceConflictModal.field}</TableCell>
                      <TableCell>{language.DataUpload.deviceConflictModal.input}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {valuesUpdated?.map((item: any) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <div className="tableCell">{item.key}</div>
                        </TableCell>
                        <TableCell>
                          <div className="tableCell">{item.value}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Styles.DropTableContainer>
          </Grid>
        </Grid>
        <Grid container sx={{ paddingTop: '20px', paddingBottom: '20px' }}>
          <Alert severity="info">{language.DataUpload.deviceConflictModal.duplicateDataUploadAlert}</Alert>
        </Grid>
        <Grid container justifyContent="space-between">
          <Button onClick={() => ignoreDuplicate()}>
            {language.DataUpload.deviceConflictModal.ignoreDuplicateData}
          </Button>
          <Button onClick={() => approveDuplicate()}>
            {language.DataUpload.deviceConflictModal.approveDuplicateData}
          </Button>
        </Grid>
      </Styles.ModalContent>
    </Dialog>
  );
};

export default MergeConflictModal;
